
import React from 'react';
import { DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info, Hourglass, Zap, CheckCircle2, Loader2 } from 'lucide-react';
import { RawResponseDisplay } from './RawResponseDisplay';
import { BackendProgressStage } from './enrichmentTypes';

interface EnrichmentPendingDialogContentProps {
  clientName: string;
  message?: string;
  lutraRunId?: string | null;
  initialLutraResponse: Record<string, unknown> | unknown[] | null;
  onClose: () => void;
  progressStage?: BackendProgressStage | string | null;
  estimatedCompletionTime?: string | null;
}

export const EnrichmentPendingDialogContent: React.FC<EnrichmentPendingDialogContentProps> = ({
  clientName,
  message,
  lutraRunId,
  initialLutraResponse,
  onClose,
  progressStage,
  estimatedCompletionTime,
}) => {
  let alertTitle = "Enrichment In Progress";
  let alertIcon = <Info className="h-4 w-4" />;

  switch (progressStage) {
    case 'queued':
      alertTitle = "Request Queued";
      alertIcon = <Hourglass className="h-4 w-4 text-yellow-500" />;
      break;
    case 'processing':
      alertTitle = "Processing Data";
      alertIcon = <Zap className="h-4 w-4 text-blue-500 animate-pulse" />;
      break;
    case 'extracting':
      alertTitle = "Extracting Information";
      alertIcon = <Zap className="h-4 w-4 text-blue-500" />;
      break;
    case 'finalizing':
      alertTitle = "Finalizing Results";
      alertIcon = <CheckCircle2 className="h-4 w-4 text-green-500 animate-pulse" />;
      break;
    case 'pending_lutra_data':
       alertTitle = "Awaiting Data from Service";
       alertIcon = <Hourglass className="h-4 w-4 text-orange-500" />;
       break;
    case 'polling_for_results':
      alertTitle = "Polling for Results";
      alertIcon = <Hourglass className="h-4 w-4 animate-spin" />;
      break;
    case 'processing_retrieved_data':
      alertTitle = "Processing Retrieved Data";
      alertIcon = <Loader2 className="h-4 w-4 animate-spin text-purple-500" />;
      break;
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Enrichment Status for {clientName}</DialogTitle>
      </DialogHeader>
      <Alert variant="default" className="my-4">
        <div className="flex items-center">
          <span className="mr-2">{alertIcon}</span>
          <AlertTitle>{alertTitle}</AlertTitle>
        </div>
        <AlertDescription className="pl-6">
          {message || "Enrichment process is ongoing. We'll update you as progress is made."}
          {estimatedCompletionTime && (
            <p className="mt-1 text-sm">{estimatedCompletionTime}</p>
          )}
          {lutraRunId && <p className="mt-1 text-xs">Lutra Run ID: {lutraRunId}</p>}
          <p className="mt-2">You can close this dialog and check back later. The client record will also reflect the latest status.</p>
        </AlertDescription>
      </Alert>
      {initialLutraResponse && (
        <RawResponseDisplay
          title="Initial Response Details (Raw)"
          data={initialLutraResponse}
          maxHeight="max-h-48"
        />
      )}
    </>
  );
};
